import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '../../../api.js';
import { useAuth } from '../../../auth/AuthProvider.js';
import type { InventoryAdjustment, Warehouse } from '../../../types/bookkeeping.js';

const REASONS = ['opening', 'count', 'damage', 'theft', 'other'] as const;

export function AdjustmentsPage() {
  const { t } = useTranslation('bookkeeping');
  const { activeOrgId } = useAuth();

  const [warehouseId, setWarehouseId] = useState('');
  const [reason, setReason] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const { data: warehousesData } = useQuery({
    queryKey: ['bk-warehouses', activeOrgId],
    queryFn: () => api<{ warehouses: Warehouse[] }>('/bookkeeping/inventory/warehouses'),
    enabled: !!activeOrgId,
  });
  const warehouses = warehousesData?.warehouses ?? [];

  const params = new URLSearchParams({ limit: '50' });
  if (warehouseId) params.set('warehouse_id', warehouseId);
  if (reason) params.set('reason', reason);
  if (fromDate) params.set('from', fromDate);
  if (toDate) params.set('to', toDate);

  const { data, isLoading } = useQuery({
    queryKey: ['bk-adjustments', activeOrgId, warehouseId, reason, fromDate, toDate],
    queryFn: () =>
      api<{ adjustments: InventoryAdjustment[] }>(
        `/bookkeeping/inventory/adjustments?${params.toString()}`,
      ),
    enabled: !!activeOrgId,
  });

  const adjustments = data?.adjustments ?? [];
  const warehouseLabel = (id: string) => {
    const w = warehouses.find((x) => x.id === id);
    return w ? `${w.code} — ${w.name}` : id;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">{t('inventory.adjustments.title')}</h1>
        <Link
          to="/app/bookkeeping/inventory/adjustments/new"
          className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
        >
          + {t('inventory.adjustments.new')}
        </Link>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-4 gap-3">
        <select
          value={warehouseId}
          onChange={(e) => setWarehouseId(e.target.value)}
          className="rounded border px-2 py-1 text-sm"
        >
          <option value="">{t('inventory.adjustments.all_warehouses')}</option>
          {warehouses.map((w) => (
            <option key={w.id} value={w.id}>
              {w.code} — {w.name}
            </option>
          ))}
        </select>
        <select
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="rounded border px-2 py-1 text-sm"
        >
          <option value="">{t('inventory.adjustments.all_reasons')}</option>
          {REASONS.map((r) => (
            <option key={r} value={r}>
              {t(`inventory.adjustments.reasons.${r}`)}
            </option>
          ))}
        </select>
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          className="rounded border px-2 py-1 text-sm"
        />
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          className="rounded border px-2 py-1 text-sm"
        />
      </div>

      {isLoading ? (
        <p className="text-sm text-slate-500">{t('common:loading')}</p>
      ) : (
        <div className="overflow-x-auto rounded border bg-white">
          <table className="w-full text-sm">
            <thead className="border-b text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">{t('inventory.adjustments.number')}</th>
                <th className="px-3 py-2">{t('inventory.adjustments.entry_date')}</th>
                <th className="px-3 py-2">{t('inventory.adjustments.warehouse')}</th>
                <th className="px-3 py-2">{t('inventory.adjustments.reason')}</th>
                <th className="px-3 py-2">{t('inventory.adjustments.memo')}</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {adjustments.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-4 text-center text-slate-400">
                    {t('inventory.adjustments.empty')}
                  </td>
                </tr>
              )}
              {adjustments.map((adj) => (
                <tr key={adj.id} className="border-b last:border-0 hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono text-xs">
                    <Link
                      to={`/app/bookkeeping/inventory/adjustments/${adj.id}`}
                      className="text-blue-600 hover:underline"
                    >
                      {adj.number}
                    </Link>
                  </td>
                  <td className="px-3 py-2">{adj.entry_date}</td>
                  <td className="px-3 py-2 text-xs">{warehouseLabel(adj.warehouse_id)}</td>
                  <td className="px-3 py-2">{t(`inventory.adjustments.reasons.${adj.reason}`)}</td>
                  <td className="px-3 py-2 text-slate-500">{adj.memo ?? '—'}</td>
                  <td className="px-3 py-2 text-right">
                    <Link
                      to={`/app/bookkeeping/inventory/adjustments/${adj.id}`}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      {t('common.view')}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
